import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Plus, Edit2, Trash2, Tag, Briefcase, Laptop, Megaphone, Plane, Zap, Users,
  Home, Wrench, FileText, Shield, MoreHorizontal, ChevronLeft, ChevronRight,
  Filter, X, Upload, Cloud, UtensilsCrossed, HeartHandshake, Repeat,
  Phone, GraduationCap, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, ReferenceArea } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { TruncatedText } from '../components/ui/truncated-text';
import { AmountInput } from '../components/ui/amount-input';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Expense categories with icons (labels: expenses:categories.<id>)
const EXPENSE_CATEGORIES = [
  { id: 'office', icon: Briefcase },
  { id: 'software', icon: Laptop },
  { id: 'cloud', icon: Cloud },
  { id: 'marketing', icon: Megaphone },
  { id: 'travel', icon: Plane },
  { id: 'utilities', icon: Zap },
  { id: 'salaries', icon: Users },
  { id: 'meals', icon: UtensilsCrossed },
  { id: 'teambuilding', icon: HeartHandshake },
  { id: 'rent', icon: Home },
  { id: 'equipment', icon: Wrench },
  { id: 'services', icon: Briefcase },
  { id: 'taxes', icon: FileText },
  { id: 'insurance', icon: Shield },
  { id: 'maintenance', icon: Wrench },
  { id: 'subscription', icon: Repeat },
  { id: 'communication', icon: Phone },
  { id: 'education', icon: GraduationCap },
  { id: 'other', icon: MoreHorizontal }
];

function Expenses() {
  const { t } = useTranslation('expenses');
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const tagInputRef = useRef(null);
  const [receiptPreview, setReceiptPreview] = useState(null);   // original URL
  const [receiptBlobUrl, setReceiptBlobUrl] = useState(null);   // blob URL for display
  const [receiptLoading, setReceiptLoading] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalAmountUsd, setTotalAmountUsd] = useState(0);
  const [totalUzsNative, setTotalUzsNative] = useState(0);
  const [totalUsdNative, setTotalUsdNative] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(null);

  // Filters — selectedYear drives chart, list, and stats
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Monthly chart
  const [monthlyStats, setMonthlyStats] = useState([]);

  // Grafana-style drag-to-select month range on the chart (zooms the chart).
  const [monthRange, setMonthRange] = useState(null); // {start, end} 1-based inclusive
  const [refLeft, setRefLeft] = useState(null);   // month number while dragging
  const [refRight, setRefRight] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    currency: 'USD',
    expense_type: 'global',
    frequency: 'monthly',
    project_id: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    percentage: '',
    description: '',
    category: '',
    tags: []
  });

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), per_page: perPage.toString() });
      if (filterMonth) params.append('month', filterMonth);
      params.append('year', selectedYear.toString());
      if (filterCategory) params.append('category', filterCategory);
      if (filterTag) params.append('tag', filterTag);

      const res = await axios.get(`${API_URL}/api/expenses?${params}`, { withCredentials: true });
      setExpenses(res.data.items);
      setTotalItems(res.data.total);
      setTotalPages(res.data.total_pages);
      setTotalAmountUsd(res.data.total_amount_usd || 0);
      setTotalUzsNative(res.data.total_uzs_native || 0);
      setTotalUsdNative(res.data.total_usd_native || 0);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error(t('toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filterMonth, selectedYear, filterCategory, filterTag, t]);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/projects`, { withCredentials: true });
      setProjects(res.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, []);

  const fetchMonthlyStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reports/monthly-trend?year=${selectedYear}`, { withCredentials: true });
      setMonthlyStats(res.data);
    } catch (error) {
      console.error('Error fetching monthly stats:', error);
    }
  }, [selectedYear]);

  const fetchTags = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/expense-tags`, { withCredentials: true });
      setAllTags(res.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
    fetchTags();
    // Fetch exchange rate for UZS display
    axios.get(`${API_URL}/api/exchange-rates/live`, { withCredentials: true })
      .then(r => { if (r.data?.rate) setExchangeRate(r.data.rate); })
      .catch(() => {});
  }, [fetchProjects, fetchTags]);

  useEffect(() => {
    fetchMonthlyStats();
  }, [fetchMonthlyStats]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [filterMonth, selectedYear, filterCategory, filterTag, perPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        percentage: formData.percentage ? parseFloat(formData.percentage) : null,
        project_id: formData.expense_type === 'project_specific' ? formData.project_id : null,
        category: formData.category || null,
        tags: formData.tags.length > 0 ? formData.tags : null
      };

      if (editingExpense) {
        await axios.patch(`${API_URL}/api/expenses/${editingExpense.expense_id}`, payload, { withCredentials: true });
        toast.success(t('toast.updated'));
      } else {
        await axios.post(`${API_URL}/api/expenses`, payload, { withCredentials: true });
        toast.success(t('toast.created'));
      }
      setOpen(false);
      setEditingExpense(null);
      resetForm();
      fetchExpenses();
      fetchTags();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error(t('toast.saveFailed'));
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/api/expenses/${expenseId}`, { withCredentials: true });
      toast.success(t('toast.deleted'));
      fetchExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error(t('toast.deleteFailed'));
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      name: expense.name,
      amount: expense.amount.toString(),
      currency: expense.currency || 'USD',
      expense_type: expense.expense_type,
      frequency: expense.frequency,
      project_id: expense.project_id || '',
      month: expense.month,
      year: expense.year,
      percentage: expense.percentage?.toString() || '',
      description: expense.description || '',
      category: expense.category || '',
      tags: expense.tags || []
    });
    setOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      amount: '',
      currency: 'USD',
      expense_type: 'global',
      frequency: 'monthly',
      project_id: '',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      percentage: '',
      description: '',
      category: '',
      tags: []
    });
    setTagInput('');
  };

  const addTag = (tag) => {
    const value = (tag || tagInput).trim().toLowerCase();
    if (value && !formData.tags.includes(value)) {
      setFormData({ ...formData, tags: [...formData.tags, value] });
    }
    setTagInput('');
    setShowTagSuggestions(false);
  };

  const removeTag = (tagToRemove) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tagToRemove) });
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
    if (e.key === 'Escape') {
      setShowTagSuggestions(false);
    }
  };

  const handleTagInputChange = (e) => {
    const val = e.target.value;
    setTagInput(val);
    if (val.trim()) {
      const filtered = allTags.filter(
        t => t.toLowerCase().includes(val.toLowerCase()) && !formData.tags.includes(t)
      );
      setTagSuggestions(filtered.slice(0, 8));
      setShowTagSuggestions(filtered.length > 0);
    } else {
      setShowTagSuggestions(false);
    }
  };

  const handleReceiptUpload = async (expenseId, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/expenses/${expenseId}/upload-receipt`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('toast.receiptUploaded'));
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toast.receiptUploadFailed'));
    }
  };

  const handleDeleteReceipt = async (expenseId) => {
    try {
      await axios.delete(`${API_URL}/api/expenses/${expenseId}/receipt`, { withCredentials: true });
      toast.success(t('toast.receiptDeleted'));
      fetchExpenses();
    } catch (error) {
      toast.error(t('toast.receiptDeleteFailed'));
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.project_id === projectId);
    return project ? project.name : t('na');
  };

  const getCategoryInfo = (categoryId) => {
    const cat = EXPENSE_CATEGORIES.find(c => c.id === categoryId);
    return cat
      ? { ...cat, name: t(`categories.${cat.id}`) }
      : { name: categoryId || t('categories.uncategorized'), icon: MoreHorizontal };
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (currency === 'UZS') {
      return `${amount?.toLocaleString() || 0} UZS`;
    }
    return `$${amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`;
  };

  const clearFilters = () => {
    setFilterMonth('');
    setFilterCategory('');
    setFilterTag('');
  };

  const hasActiveFilters = filterMonth || filterCategory || filterTag || selectedYear !== new Date().getFullYear();

  const months = t('common:months', { returnObjects: true });
  const monthsShort = t('common:monthsShort', { returnObjects: true });

  // Chart data — zooms to the drag-selected month range when one is applied.
  const chartData = monthRange
    ? monthlyStats.filter((m) => m.month >= monthRange.start && m.month <= monthRange.end)
    : monthlyStats;

  // Commit an in-progress drag (month numbers) to a real range filter.
  const finishRangeSelect = () => {
    if (refLeft != null && refRight != null && refLeft !== refRight) {
      setMonthRange({ start: Math.min(refLeft, refRight), end: Math.max(refLeft, refRight) });
    }
    setRefLeft(null);
    setRefRight(null);
  };
  const clearMonthRange = () => { setMonthRange(null); setRefLeft(null); setRefRight(null); };

  if (loading && expenses.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
            {t('title')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
            {t('subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[100px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="relative">
            <Filter className="h-4 w-4 mr-2" />
            {t('filters.title')}
            {hasActiveFilters && (
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-blue-500 rounded-full text-[10px] text-white flex items-center justify-center">
                !
              </span>
            )}
          </Button>
          <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) { setEditingExpense(null); resetForm(); } }}>
            <DialogTrigger asChild>
              <Button onClick={() => setOpen(true)} data-testid="add-expense-btn">
                <Plus className="h-4 w-4 mr-2" />
                {t('addExpense')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingExpense ? t('dialog.editTitle') : t('dialog.createTitle')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">{t('form.name')} *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t('form.namePlaceholder')}
                    required
                    data-testid="expense-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 items-end">
                  <div>
                    <Label htmlFor="amount">{t('common:fields.amount')} *</Label>
                    <AmountInput
                      id="amount"
                      value={formData.amount}
                      onChange={(val) => setFormData({ ...formData, amount: val })}
                      placeholder="0.00"
                      required
                      data-testid="expense-amount-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="currency">{t('common:fields.currency')}</Label>
                    <Select value={formData.currency} onValueChange={(val) => setFormData({ ...formData, currency: val })}>
                      <SelectTrigger data-testid="expense-currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="UZS">{t('form.uzsOption')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Category */}
                <div>
                  <Label htmlFor="category">{t('form.category')}</Label>
                  <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                    <SelectTrigger data-testid="expense-category-select">
                      <SelectValue placeholder={t('form.selectCategory')} />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPENSE_CATEGORIES.map((cat) => {
                        const Icon = cat.icon;
                        return (
                          <SelectItem key={cat.id} value={cat.id}>
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              {t(`categories.${cat.id}`)}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {/* Tags with suggestions */}
                <div className="relative">
                  <Label>{t('form.tags')}</Label>
                  <div className="flex gap-2 mt-2">
                    <div className="relative flex-1">
                      <Input
                        ref={tagInputRef}
                        value={tagInput}
                        onChange={handleTagInputChange}
                        onKeyDown={handleTagKeyDown}
                        onFocus={() => {
                          if (tagInput.trim()) {
                            const filtered = allTags.filter(
                              t => t.toLowerCase().includes(tagInput.toLowerCase()) && !formData.tags.includes(t)
                            );
                            setTagSuggestions(filtered.slice(0, 8));
                            setShowTagSuggestions(filtered.length > 0);
                          }
                        }}
                        onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                        placeholder={t('form.tagPlaceholder')}
                        data-testid="tag-input"
                      />
                      {showTagSuggestions && (
                        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg max-h-40 overflow-y-auto">
                          {tagSuggestions.map((s, idx) => (
                            <button
                              key={idx}
                              type="button"
                              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300"
                              onMouseDown={(e) => { e.preventDefault(); addTag(s); }}
                            >
                              <Tag className="h-3 w-3 inline mr-2 text-slate-400" />
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button type="button" variant="outline" onClick={() => addTag()}>
                      <Tag className="h-4 w-4" />
                    </Button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.tags.map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                          {tag} ×
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="expense_type">{t('form.type')}</Label>
                    <Select value={formData.expense_type} onValueChange={(val) => setFormData({ ...formData, expense_type: val })}>
                      <SelectTrigger data-testid="expense-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="global">{t('types.global')}</SelectItem>
                        <SelectItem value="project_specific">{t('types.projectSpecific')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="frequency">{t('form.frequency')}</Label>
                    <Select value={formData.frequency} onValueChange={(val) => setFormData({ ...formData, frequency: val })}>
                      <SelectTrigger data-testid="expense-frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="one-time">{t('frequency.oneTime')}</SelectItem>
                        <SelectItem value="monthly">{t('frequency.monthly')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {formData.expense_type === 'project_specific' && (
                  <div>
                    <Label htmlFor="project_id">{t('common:fields.project')}</Label>
                    <Select value={formData.project_id} onValueChange={(val) => setFormData({ ...formData, project_id: val })}>
                      <SelectTrigger data-testid="expense-project-select">
                        <SelectValue placeholder={t('form.selectProject')} />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((project) => (
                          <SelectItem key={project.project_id} value={project.project_id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="month">{t('common:fields.month')}</Label>
                    <Select value={formData.month.toString()} onValueChange={(val) => setFormData({ ...formData, month: parseInt(val) })}>
                      <SelectTrigger data-testid="expense-month-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map((month, idx) => (
                          <SelectItem key={idx} value={(idx + 1).toString()}>
                            {month}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="year">{t('common:fields.year')}</Label>
                    <Select value={formData.year.toString()} onValueChange={(val) => setFormData({ ...formData, year: parseInt(val) })}>
                      <SelectTrigger data-testid="expense-year-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map((year) => (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">{t('form.descriptionOptional')}</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={t('form.descriptionPlaceholder')}
                    data-testid="expense-description-input"
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit" data-testid="submit-expense-btn">
                    {editingExpense ? t('form.submitUpdate') : t('form.submitCreate')}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { setOpen(false); setEditingExpense(null); resetForm(); }}>
                    {t('common:actions.cancel')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Monthly Expenses Chart */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base font-semibold">{t('chart.title', { year: selectedYear })}</CardTitle>
          {monthRange ? (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 dark:bg-red-900/30 px-3 py-1 text-xs font-medium text-red-700 dark:text-red-400" data-testid="expense-month-range-chip">
                {months[monthRange.start - 1]} – {months[monthRange.end - 1]}
              </span>
              <button onClick={clearMonthRange} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" data-testid="clear-expense-month-range">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <span className="text-xs text-slate-400 dark:text-slate-500 select-none hidden sm:inline">
              💡 {t('chart.dragHint')}
            </span>
          )}
        </CardHeader>
        <CardContent>
          {monthlyStats.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={chartData}
                margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
                onMouseDown={(e) => { if (e && e.activeLabel != null) { setRefLeft(e.activeLabel); setRefRight(e.activeLabel); } }}
                onMouseMove={(e) => { if (refLeft != null && e && e.activeLabel != null) setRefRight(e.activeLabel); }}
                onMouseUp={finishRangeSelect}
                onMouseLeave={finishRangeSelect}
                style={{ cursor: 'crosshair', userSelect: 'none' }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="month"
                  stroke="#64748b"
                  fontSize={12}
                  tickFormatter={(m) => monthsShort[m - 1]}
                />
                <YAxis
                  stroke="#64748b"
                  fontSize={11}
                  tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`}
                />
                <Tooltip
                  labelFormatter={(m) => months[m - 1]}
                  formatter={(value, name) => [
                    `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                    name
                  ]}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                />
                <Legend />
                <Bar dataKey="operational_expenses" name={t('chart.operational')} fill="#ef4444" radius={[4, 4, 0, 0]} />
                {refLeft != null && refRight != null && (
                  <ReferenceArea x1={refLeft} x2={refRight} stroke="#ef4444" strokeOpacity={0.9} strokeWidth={1.5} fill="#ef4444" fillOpacity={0.28} />
                )}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-32 text-slate-400 text-sm">{t('chart.noData', { year: selectedYear })}</div>
          )}
          {/* Summary row — reflects the zoomed range when one is applied */}
          {chartData.length > 0 && (() => {
            const total = chartData.reduce((s, m) => s + m.operational_expenses, 0);
            const maxMonth = chartData.reduce((max, m) => m.operational_expenses > max.operational_expenses ? m : max, chartData[0]);
            const monthNames = months;
            const divisor = chartData.length || 1;
            return (
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-xs text-slate-500">
                    {t('chart.total', { period: monthRange ? `${monthsShort[monthRange.start - 1]}–${monthsShort[monthRange.end - 1]}` : selectedYear })}
                  </div>
                  <div className="text-sm font-semibold text-red-600">${total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">{t('chart.monthlyAvg')}</div>
                  <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">${(total / divisor).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">{t('chart.highestMonth')}</div>
                  <div className="text-sm font-semibold text-orange-600">{monthNames[maxMonth.month - 1]}</div>
                </div>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Filters Bar */}
      {showFilters && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">{t('filters.title')}</h3>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs">
                <X className="h-3 w-3 mr-1" /> {t('filters.clearAll')}
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">{t('common:fields.month')}</Label>
              <Select value={filterMonth} onValueChange={setFilterMonth}>
                <SelectTrigger className="h-9"><SelectValue placeholder={t('filters.allMonths')} /></SelectTrigger>
                <SelectContent>
                  {months.map((m, idx) => (
                    <SelectItem key={idx} value={(idx + 1).toString()}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('form.category')}</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="h-9"><SelectValue placeholder={t('filters.allCategories')} /></SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{t(`categories.${cat.id}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t('filters.tag')}</Label>
              <Select value={filterTag} onValueChange={setFilterTag}>
                <SelectTrigger className="h-9"><SelectValue placeholder={t('filters.allTags')} /></SelectTrigger>
                <SelectContent>
                  {allTags.map((tag) => (
                    <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Summary Card */}
      {totalItems > 0 && (
        <div className="bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 rounded-xl border border-red-200/60 dark:border-red-800/40 p-4 shadow-sm">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <div className="text-xs text-slate-500 dark:text-slate-400">{t('summary.total')}</div>
              <div className="text-lg font-bold text-red-600 dark:text-red-400">
                {exchangeRate
                  ? `${Math.round(totalUzsNative + totalUsdNative * exchangeRate).toLocaleString()} UZS`
                  : `$${totalAmountUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                }
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 dark:text-slate-400">{t('summary.records')}</div>
              <div className="text-lg font-bold text-slate-700 dark:text-slate-300">{totalItems}</div>
            </div>
            {totalUsdNative > 0 && (
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">{t('summary.usdNative')}</div>
                <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                  ${totalUsdNative.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            )}
            {totalUzsNative > 0 && (
              <div>
                <div className="text-xs text-slate-500 dark:text-slate-400">{t('summary.uzsNative')}</div>
                <div className="text-lg font-bold text-orange-600 dark:text-orange-400">
                  {Math.round(totalUzsNative).toLocaleString()} UZS
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Expenses Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('common:fields.name')}</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('form.category')}</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('common:fields.amount')}</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('form.type')}</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('form.tags')}</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('table.period')}</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('table.receipt')}</th>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('common:fields.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                    {hasActiveFilters ? t('table.emptyFiltered', { period: `${selectedYear}${filterMonth ? ' / ' + months[parseInt(filterMonth) - 1] : ''}` }) : t('table.empty')}
                  </td>
                </tr>
              ) : (
                expenses.map((expense) => {
                  const categoryInfo = getCategoryInfo(expense.category);
                  const CategoryIcon = categoryInfo.icon;
                  return (
                    <tr
                      key={expense.expense_id}
                      className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                      data-testid={`expense-row-${expense.expense_id}`}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{expense.name}</div>
                        {expense.description && (
                          <TruncatedText text={expense.description} maxLength={35} className="text-xs text-slate-500 truncate max-w-[200px] block" />
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <CategoryIcon className="h-4 w-4 text-slate-500" />
                          <span className="text-sm text-slate-700 dark:text-slate-300">{categoryInfo.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-red-600 dark:text-red-400">
                          {formatCurrency(expense.amount, expense.currency)}
                        </div>
                        {expense.currency !== 'USD' && expense.converted_amount && (
                          <div className="text-xs text-slate-500">
                            ≈ {formatCurrency(expense.converted_amount, 'USD')}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          expense.expense_type === 'global'
                            ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400'
                            : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                        }`}>
                          {expense.expense_type === 'global' ? t('types.global') : getProjectName(expense.project_id)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1 max-w-[150px]">
                          {expense.tags && expense.tags.length > 0 ? (
                            expense.tags.slice(0, 3).map((tag, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">{tag}</Badge>
                            ))
                          ) : (
                            <span className="text-xs text-slate-400">-</span>
                          )}
                          {expense.tags && expense.tags.length > 3 && (
                            <Badge variant="outline" className="text-xs">+{expense.tags.length - 3}</Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300 whitespace-nowrap">
                        {months[expense.month - 1]} {expense.year}
                      </td>
                      <td className="px-6 py-4">
                        {expense.receipt_image ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={async () => {
                                const url = `${API_URL}${expense.receipt_image}`;
                                setReceiptPreview(url);
                                setReceiptBlobUrl(null);
                                setReceiptLoading(true);
                                try {
                                  const res = await fetch(url, { credentials: 'include' });
                                  const blob = await res.blob();
                                  setReceiptBlobUrl(URL.createObjectURL(blob));
                                } catch {
                                  setReceiptBlobUrl(url); // fallback to direct URL
                                } finally {
                                  setReceiptLoading(false);
                                }
                              }}
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                              title={t('receipt.view')}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteReceipt(expense.expense_id)}
                              className="text-red-500 hover:text-red-700"
                              title={t('receipt.delete')}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <label className="cursor-pointer text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" title={t('receipt.upload')}>
                            <Upload className="h-4 w-4" />
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*,.pdf"
                              onChange={(e) => {
                                if (e.target.files[0]) handleReceiptUpload(expense.expense_id, e.target.files[0]);
                              }}
                            />
                          </label>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(expense)} data-testid={`edit-expense-${expense.expense_id}`}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(expense.expense_id)} data-testid={`delete-expense-${expense.expense_id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalItems > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600 dark:text-slate-400">
                {t('pagination.showing', { from: ((page - 1) * perPage) + 1, to: Math.min(page * perPage, totalItems), total: totalItems })}
              </span>
              <Select value={perPage.toString()} onValueChange={(val) => setPerPage(parseInt(val))}>
                <SelectTrigger className="w-[100px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">{t('pagination.perPage', { n: 10 })}</SelectItem>
                  <SelectItem value="20">{t('pagination.perPage', { n: 20 })}</SelectItem>
                  <SelectItem value="50">{t('pagination.perPage', { n: 50 })}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={page === pageNum ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPage(pageNum)}
                    className="w-8 h-8 p-0"
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Receipt Preview Modal */}
      {receiptPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => {
            if (receiptBlobUrl && receiptBlobUrl.startsWith('blob:')) URL.revokeObjectURL(receiptBlobUrl);
            setReceiptPreview(null);
            setReceiptBlobUrl(null);
          }}
        >
          <div className="relative max-w-3xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            {/* Controls */}
            <div className="absolute -top-9 right-0 flex items-center gap-2">
              <a
                href={receiptPreview}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded px-2 py-1 transition-colors"
              >
                ↗ {t('receipt.openInNewTab')}
              </a>
              <button
                className="bg-white dark:bg-slate-800 rounded-full p-1 shadow-lg hover:bg-slate-100"
                onClick={() => {
                  if (receiptBlobUrl && receiptBlobUrl.startsWith('blob:')) URL.revokeObjectURL(receiptBlobUrl);
                  setReceiptPreview(null);
                  setReceiptBlobUrl(null);
                }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {receiptLoading ? (
              <div className="w-[720px] h-[82vh] rounded-lg bg-white flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
              </div>
            ) : receiptPreview.endsWith('.pdf') ? (
              <iframe
                src={receiptBlobUrl || receiptPreview}
                className="w-[720px] h-[82vh] rounded-lg border-0 bg-white"
                title={t('receipt.title')}
              />
            ) : (
              <img
                src={receiptBlobUrl || receiptPreview}
                alt={t('receipt.title')}
                className="max-h-[82vh] rounded-lg shadow-2xl object-contain"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Expenses;
